import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Typography, Button, Paper, Box, LinearProgress, Alert } from "@mui/material";
import { storage, db } from "../../Firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface UploadStatus {
  total: number;
  completed: number;
  current: string;
  error?: string;
}

const UploadTakeImages: React.FC = () => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    total: 0,
    completed: 0,
    current: ""
  });

  const uploadImage = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const handleUpload = async () => {
    setUploading(true);
    setUploadStatus({ total: 0, completed: 0, current: "準備中..." });

    try {
      const takeImages = [
        { name: "taketitle.PNG", path: "title" },
        { name: "takerule.PNG", path: "rule" },
        { name: "takejunbi.png", path: "junbi" },
        { name: "takeend.png", path: "end" },
        ...Array.from({ length: 11 }, (_, i) => ({ name: `take${i}.PNG`, path: `problem_${i}` })),
        ...Array.from({ length: 11 }, (_, i) => ({ name: `take${i}a.PNG`, path: `answer_${i}`, isAnswer: true }))
      ];

      const totalImages = takeImages.length;
      setUploadStatus({ total: totalImages, completed: 0, current: "画像をアップロード中..." });

      const metadata: any = {
        setId: "take-set",
        setName: "takeセット",
        creator: "take",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        images: {},
        problems: [],
        isActive: true,
        description: "takeによる謎解きセット"
      };

      // Create file input to select images
      const input = document.createElement("input");
      input.type = "file";
      input.multiple = true;
      input.accept = "image/*";
      input.webkitdirectory = true;

      input.onchange = async (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || []);
        const takeFiles = files.filter(file => file.name.includes("take"));

        for (let i = 0; i < takeFiles.length; i++) {
          const file = takeFiles[i];
          const fileName = file.name;
          setUploadStatus(prev => ({ ...prev, current: `${fileName} をアップロード中...`, completed: i }));

          try {
            if (fileName === "taketitle.PNG") {
              const url = await uploadImage(file, `v3arena/sets/take/taketitle.PNG`);
              metadata.images.title = url;
            } else if (fileName === "takerule.PNG") {
              const url = await uploadImage(file, `v3arena/sets/take/takerule.PNG`);
              metadata.images.rule = url;
            } else if (fileName === "takejunbi.png") {
              const url = await uploadImage(file, `v3arena/sets/take/takejunbi.png`);
              metadata.images.junbi = url;
            } else if (fileName === "takeend.png") {
              const url = await uploadImage(file, `v3arena/sets/take/takeend.png`);
              metadata.images.end = url;
            } else if (fileName.match(/^take(\d+)\.PNG$/)) {
              const num = parseInt(fileName.match(/^take(\d+)\.PNG$/)![1]);
              const url = await uploadImage(file, `v3arena/sets/take/problems/take${num}.PNG`);
              
              let problem = metadata.problems.find((p: any) => p.problemNumber === num);
              if (!problem) {
                problem = {
                  problemId: `take-${num}`,
                  problemNumber: num,
                  images: {}
                };
                metadata.problems.push(problem);
              }
              problem.images.problem = url;
            } else if (fileName.match(/^take(\d+)a\.PNG$/)) {
              const num = parseInt(fileName.match(/^take(\d+)a\.PNG$/)![1]);
              const url = await uploadImage(file, `v3arena/sets/take/problems/take${num}a.PNG`);
              
              let problem = metadata.problems.find((p: any) => p.problemNumber === num);
              if (!problem) {
                problem = {
                  problemId: `take-${num}`,
                  problemNumber: num,
                  images: {}
                };
                metadata.problems.push(problem);
              }
              problem.images.answer = url;
            }
          } catch (error) {
            console.error(`Error uploading ${fileName}:`, error);
          }
        }

        // Sort problems by number
        metadata.problems.sort((a: any, b: any) => a.problemNumber - b.problemNumber);
        metadata.problemCount = metadata.problems.length;

        setUploadStatus(prev => ({ ...prev, current: "Firestoreに保存中..." }));

        // Save to Firestore
        await setDoc(doc(db, "v3sets", "take-set"), metadata);

        setUploadStatus({
          total: takeFiles.length,
          completed: takeFiles.length,
          current: "アップロード完了!"
        });

        setTimeout(() => {
          navigate("/admin");
        }, 2000);
      };

      input.click();
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus(prev => ({
        ...prev,
        error: "アップロード中にエラーが発生しました"
      }));
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        takeセット画像アップロード
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="body1" paragraph>
          data/takeディレクトリの画像をFirebase Storageにアップロードします。
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          アップロードする画像:
          <ul>
            <li>taketitle.PNG - タイトル画像</li>
            <li>takerule.PNG - ルール説明画像</li>
            <li>takejunbi.png - 準備画像</li>
            <li>takeend.png - 終了画像</li>
            <li>take0.PNG〜take10.PNG - 問題画像</li>
            <li>take0a.PNG〜take10a.PNG - 解答画像</li>
          </ul>
        </Typography>

        {!uploading ? (
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={handleUpload}
            fullWidth
          >
            takeフォルダを選択してアップロード
          </Button>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {uploadStatus.current}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploadStatus.total > 0 ? (uploadStatus.completed / uploadStatus.total) * 100 : 0}
              sx={{ mt: 2, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {uploadStatus.completed} / {uploadStatus.total}
            </Typography>
          </Box>
        )}

        {uploadStatus.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {uploadStatus.error}
          </Alert>
        )}
      </Paper>

      <Button
        variant="outlined"
        onClick={() => navigate("/admin")}
        sx={{ mt: 2 }}
      >
        管理画面に戻る
      </Button>
    </Container>
  );
};

export default UploadTakeImages;